import { useEffect, useRef, useState } from 'react';
import AttachmentPreviewContent from 'components/Dashboard/components/AttachmentPreviewContent';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';

interface SharedPdfPreviewProps {
  url: string;
  filename?: string;
}

interface AttachmentNavButtonProps {
  direction: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
}

interface PdfLoadSuccessEvent {
  numPages: number;
}

const AttachmentNavButton = ({ direction, disabled, onClick }: AttachmentNavButtonProps) => (
  <button
    type='button'
    className={`general-button ${direction === 'prev' ? 'total-contributions-prev' : 'total-contributions-next'}`}
    disabled={disabled}
    onClick={onClick}
    aria-label={direction === 'prev' ? 'Previous page' : 'Next page'}
    style={{ width: '22px', height: '22px' }}
  >
    <span className={direction === 'prev' ? 'icon-arrow-back' : 'icon-arrow-next'} />
  </button>
);

const SharedPdfPreview = ({ url, filename }: SharedPdfPreviewProps) => {
  const [pdfPage, setPdfPage] = useState<number>(1);
  const [pdfPages, setPdfPages] = useState<number>(0);
  const [isMoreOpen, setIsMoreOpen] = useState<boolean>(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  useOutsideElement(moreMenuRef, () => setIsMoreOpen(false));

  useEffect(() => {
    setPdfPage(1);
    setPdfPages(0);
  }, [url]);

  return (
    <div className='overflow-hidden rounded'>
      <div className='d-flex align-items-center justify-content-end gap-15 px-3 py-2 border-general specific-bottom-margin-12 flex-wrap border-grey-50 rounded bg-grey'>
        {pdfPages > 0 && (
          <div className='d-flex align-items-center border specific-top-padding-4 specific-bottom-padding-5 specific-x-padding-8 h-100 rounded-pill total-contributions-buttons bg-white'>
            <AttachmentNavButton
              direction='prev'
              disabled={pdfPage <= 1}
              onClick={() => setPdfPage((prev) => Math.max(1, prev - 1))}
            />
            <span className='nowrap-item paragraph-p3 main-subtle-text mx-3'>
              Page {pdfPage} of {pdfPages}
            </span>
            <AttachmentNavButton
              direction='next'
              disabled={pdfPage >= pdfPages}
              onClick={() => setPdfPage((prev) => Math.min(pdfPages, prev + 1))}
            />
          </div>
        )}

        <div className='position-relative' ref={moreMenuRef}>
          <button
            type='button'
            className='general-button px-3 specific-y-padding-5 border-general border-grey-50 rounded d-flex align-items-center rounded-pill bg-main-white hovered-button'
            onClick={() => setIsMoreOpen((prev) => !prev)}
          >
            <span className={`dropdown-item-element dropdown-item-element-10 ml-2 ${isMoreOpen ? 'open' : ''}`}>
              More{' '}
            </span>
          </button>
          {isMoreOpen && (
            <div
              className='position-absolute top-45 right-0 box-shadow rounded bg-main-white py-2 z-100'
              style={{ width: '150px' }}
            >
              <button
                type='button'
                className='general-button list-action-button w-100 text-left px-3 py-2'
                onClick={() => {
                  window.open(url, '_blank', 'noopener,noreferrer');
                  setIsMoreOpen(false);
                }}
              >
                Open in new tab
              </button>
            </div>
          )}
        </div>
      </div>

      <AttachmentPreviewContent
        url={url}
        filename={filename}
        pageNumber={pdfPage}
        onPageNumberChange={setPdfPage}
        onPdfLoadSuccess={({ numPages }: PdfLoadSuccessEvent) => {
          setPdfPages(numPages);
          setPdfPage((prev) => Math.min(prev, numPages || 1));
        }}
        showControls={false}
        setMaxHeight={false}
      />
    </div>
  );
};

export default SharedPdfPreview;
